import React, { useCallback, useEffect, useState } from 'react';
import { IDialogProps } from '../../../lib/dialog/Dialog';
import { EditorService } from '../../../services/editor';
import {
	EntityId,
	EntityType,
	VariableDeclarationState,
} from '@elyx-code/project-logic-tree';
import { useParams } from 'react-router-dom';
import VariableDeclaration from './VariableDeclaration';
import DialogLoader from '../../DialogLoader';
import { popupNotification } from '../../../popup-notification';
import EntityDialogHeader from '../../EntityDialogHeader';

export interface IVariableDeclarationDialogContentProps extends IDialogProps {
	project: EditorService;
	ready: boolean;
}

export interface IVariableDeclarationSectionProps {
	entity: VariableDeclarationState;
	project: EditorService;
}

const VariableDeclarationDialogContent: React.FC<
	IVariableDeclarationDialogContentProps
> = ({ onClose, project, ready }) => {
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: VariableDeclarationState | null =
		(project?.logic?.get(entityId) as VariableDeclarationState | null) || null;

	const onEscapePressed = useCallback(
		(e: KeyboardEvent) => {
			// Or close the dialog if there is only one tab
			onClose(e);
		},
		[onClose]
	);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('escape-key-press', onEscapePressed);

		if (!!entity) {
			project?.on(entity.id, () => {
				forceUpdate();
			});
		}

		return () => {
			project?.off('escape-key-press', onEscapePressed);
		};
	}, [project, entity, onEscapePressed]);

	if (!project || !entity) {
		if (!!project && project.hasLoadedLogic && !entity) {
			popupNotification({
				color: 'error',
				text: 'This variable declaration does not exist in the current version of the project',
			});
			onClose(null);
			return;
		}

		return (
			<DialogLoader
				title='Variable declaration'
				subtitle='Variable declaration builder'
				onClose={onClose}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				paddingBottom: 20,
			}}
		>
			<EntityDialogHeader entity={entity} project={project} onClose={onClose} />

			<VariableDeclaration
				entity={entity}
				project={project}
				onClose={(e) => {
					onClose(e);
				}}
			/>
		</div>
	);
};

export default VariableDeclarationDialogContent;
